import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Send, ImagePlus, X, LogOut, User, Edit2, Trash2, MessageCircle, Check, Phone, Heart, Flame, Leaf, ThumbsUp, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useLanguage } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { CommunityLoadingAnimation } from "../components/CommunityLoadingAnimation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "972528614433";

// Reaction types with icons and colors
const REACTION_TYPES = [
  { type: 'heart', icon: Heart, emoji: '❤️', color: 'text-red-500', bgColor: 'bg-red-50 dark:bg-red-900/20', activeColor: 'bg-red-100 dark:bg-red-900/40' },
  { type: 'fire', icon: Flame, emoji: '🔥', color: 'text-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-900/20', activeColor: 'bg-orange-100 dark:bg-orange-900/40' },
  { type: 'leaf', icon: Leaf, emoji: '🌿', color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/20', activeColor: 'bg-green-100 dark:bg-green-900/40' },
  { type: 'thumbs_up', icon: ThumbsUp, emoji: '👍', color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/20', activeColor: 'bg-blue-100 dark:bg-blue-900/40' },
  { type: 'sparkle', icon: Sparkles, emoji: '✨', color: 'text-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', activeColor: 'bg-yellow-100 dark:bg-yellow-900/40' },
];

// Helper to get auth headers
const getAuthHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const storedToken = localStorage.getItem('mart_session_token');
  if (storedToken) {
    headers['Authorization'] = `Bearer ${storedToken}`;
  }
  return headers;
};

export const Community = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState("");
  const [newPostImages, setNewPostImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [postReactions, setPostReactions] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsLoading(false);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    checkAuth();
  }, [location, navigate]);

  useEffect(() => {
    fetchPosts();
  }, []);

  const checkAuth = async () => {
    try {
      // Get token from localStorage (for cross-domain support)
      const storedToken = localStorage.getItem('mart_session_token');
      
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Clear invalid token
        localStorage.removeItem('mart_session_token');
      }
    } catch (error) {
      console.log("Not authenticated");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
        // Fetch reactions for each post
        data.forEach(post => fetchReactions(post.post_id));
      }
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const fetchReactions = async (postId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts/${postId}/reactions`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPostReactions(prev => ({ ...prev, [postId]: data }));
      }
    } catch (error) {
      console.error("Failed to fetch reactions:", error);
    }
  };

  const handleReaction = async (postId, reactionType) => {
    if (!user) {
      handleLogin();
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts/${postId}/reactions`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({ reaction_type: reactionType }),
      });
      
      if (response.ok) {
        // Refresh reactions for this post
        fetchReactions(postId);
      }
    } catch (error) {
      console.error("Failed to add reaction:", error);
    }
    setShowReactionPicker(null);
  };

  const handleLogin = () => {
    // Save current page to return to after login
    localStorage.setItem('mart_auth_redirect', '/community');
    const redirectUrl = window.location.origin + "/community";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      const storedToken = localStorage.getItem('mart_session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers,
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear localStorage token
    localStorage.removeItem('mart_session_token');
    setUser(null);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (newPostImages.length + files.length > 3) {
      alert(language === 'he' ? "ניתן להעלות עד 3 תמונות" : "You can upload up to 3 images");
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewPostImages((prev) => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setNewPostImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitPost = async () => {
    if (!newPostContent.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({
          content: newPostContent,
          images: newPostImages,
        }),
      });

      if (response.ok) {
        setNewPostContent("");
        setNewPostImages([]);
        setShowSuccessModal(true);
        fetchPosts();
      }
    } catch (error) {
      console.error("Failed to create post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    const confirmMsg = language === 'he' ? 'האם אתה בטוח שברצונך למחוק את הפוסט?' : 'Are you sure you want to delete this post?';
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts/${postId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: "include",
      });
      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error("Failed to delete post:", error);
    }
  };

  const handleEditPost = (post) => {
    setEditingPostId(post.post_id);
    setEditContent(post.content);
  };

  const handleSaveEdit = async (postId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts/${postId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({ content: editContent, images: [] }),
      });
      if (response.ok) {
        setEditingPostId(null);
        setEditContent("");
        fetchPosts();
      }
    } catch (error) {
      console.error("Failed to update post:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingPostId(null);
    setEditContent("");
  };

  const fetchComments = async (postId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts/${postId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setExpandedComments((prev) => ({ ...prev, [postId]: data }));
      }
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    }
  };

  const toggleComments = (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments((prev) => {
        const newState = { ...prev };
        delete newState[postId];
        return newState;
      });
    } else {
      fetchComments(postId);
    }
  };

  const handleSubmitComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content?.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/community/posts/${postId}/comments`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        setCommentInputs((prev) => ({ ...prev, [postId]: "" }));
        fetchComments(postId);
      }
    } catch (error) {
      console.error("Failed to create comment:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'he' ? "he-IL" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const openWhatsApp = () => {
    const message = language === 'he' 
      ? 'שלום, שיתפתי ניסוי בקהילה ואשמח לדבר על החוויה שלי עם הדאודורנט'
      : 'Hello, I shared an experiment in the community and would like to discuss my experience';
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Success Modal Component
  const SuccessModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-fade-in-up">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#4A5D43]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#4A5D43]" />
          </div>
          <h3 className="font-serif text-2xl text-[#2C3329] mb-2">
            {language === 'he' ? 'הפוסט פורסם בהצלחה!' : 'Post Published Successfully!'}
          </h3>
          <p className="text-[#6B7266] mb-6">
            {language === 'he' 
              ? 'תודה על השיתוף! הניסוי שלך יעזור לאחרים בקהילה.'
              : 'Thank you for sharing! Your experiment will help others in the community.'}
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={openWhatsApp}
              className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full py-5"
            >
              <Phone className={`h-4 w-4 ${language === 'he' ? 'ml-2' : 'mr-2'}`} />
              {language === 'he' ? 'צור קשר עם היצרן בוואטסאפ' : 'Contact Manufacturer on WhatsApp'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSuccessModal(false)}
              className="w-full border-[#E8E4DC] text-[#6B7266] rounded-full py-5"
            >
              {language === 'he' ? 'סגור' : 'Close'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-24 pb-12">
      <SEO page="community" />
      {/* Success Modal */}
      {showSuccessModal && <SuccessModal />}

      {/* Hero Header */}
      <section className="relative px-4 sm:px-6 mb-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A5D43]/5 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative">
          <span className="inline-block px-4 py-1.5 bg-[#4A5D43]/10 text-[#4A5D43] text-xs font-medium rounded-full mb-4 tracking-wider uppercase animate-fade-in">
            {language === 'he' ? 'מעבדה משותפת' : 'Shared Lab'}
          </span>
          <h1 
            data-testid="community-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C3329] mb-4 animate-fade-in-up"
          >
            {t.community.title}
          </h1>
          <p className="text-lg text-[#6B7266] mb-2 animate-fade-in-up animation-delay-200 max-w-2xl mx-auto">
            {t.community.subtitle}
          </p>
          <p className="text-sm text-[#9A9790] animate-fade-in-up animation-delay-300">
            {language === 'he' 
              ? `${posts.length} ניסויים משותפים עד כה`
              : `${posts.length} experiments shared so far`}
          </p>
          <div className="decorative-line mt-6" />
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Posts Feed - FIRST */}
        <div className="space-y-5 mb-8">
          <h2 className="font-serif text-xl text-[#2C3329] mb-4">
            {language === 'he' ? 'ניסויים אחרונים' : 'Recent Experiments'}
          </h2>
          
          {isLoadingPosts ? (
            <CommunityLoadingAnimation />
          ) : posts.length === 0 ? (
            <div className="text-center py-16 premium-card rounded-2xl">
              <div className="w-16 h-16 bg-[#4A5D43]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#4A5D43]" />
              </div>
              <p className="text-[#6B7266]">{t.community.empty}</p>
              <p className="text-sm text-[#9A9790] mt-2">
                {language === 'he' ? 'היו הראשונים לשתף!' : 'Be the first to share!'}
              </p>
            </div>
          ) : (
            posts.map((post) => (
              <div 
                key={post.post_id}
                data-testid={`post-${post.post_id}`}
                className="premium-card rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                {/* Post Header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-4">
                    <Avatar className="border-2 border-[#E8E4DC]">
                      <AvatarImage src={post.user_picture} alt={post.user_name} />
                      <AvatarFallback className="bg-[#4A5D43]/10">
                        <User className="h-4 w-4 text-[#4A5D43]" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-[#2C3329]">{post.user_name}</p>
                      <p className="text-xs text-[#9A9790]">{formatDate(post.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Edit/Delete buttons for post owner */}
                  {user && user.user_id === post.user_id && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditPost(post)}
                        className="p-2 text-[#6B7266] hover:text-[#4A5D43] hover:bg-[#4A5D43]/5 rounded-full transition-colors"
                        data-testid={`edit-post-${post.post_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.post_id)}
                        className="p-2 text-[#6B7266] hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        data-testid={`delete-post-${post.post_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Post Content */}
                {editingPostId === post.post_id ? (
                  <div className="mb-5">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="mb-3 bg-[#FAF8F5] border-[#E8E4DC] focus:border-[#4A5D43] resize-none min-h-[100px] rounded-xl"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSaveEdit(post.post_id)}
                        size="sm"
                        className="btn-primary rounded-full px-4"
                      >
                        {language === 'he' ? 'שמור' : 'Save'}
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        size="sm"
                        className="rounded-full px-4"
                      >
                        {language === 'he' ? 'ביטול' : 'Cancel'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-[#2C3329] mb-5 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                )}

                {/* Post Images */}
                {post.images && post.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {post.images.map((img, index) => (
                      <img 
                        key={index}
                        src={img}
                        alt={`${language === 'he' ? 'תמונה' : 'Image'} ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                      />
                    ))}
                  </div>
                )}

                {/* Reactions Section - ALWAYS VISIBLE */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#E8E4DC] dark:border-[#2a3a2a]">
                  <div className="flex items-center gap-1 flex-wrap">
                    {/* Reaction counts display */}
                    {postReactions[post.post_id]?.counts && Object.keys(postReactions[post.post_id].counts).length > 0 ? (
                      REACTION_TYPES.filter(r => postReactions[post.post_id]?.counts[r.type] > 0).map(reaction => {
                        const count = postReactions[post.post_id]?.counts[reaction.type] || 0;
                        const isUserReaction = postReactions[post.post_id]?.user_reactions?.includes(reaction.type);
                        const Icon = reaction.icon;
                        
                        return (
                          <button
                            key={reaction.type}
                            onClick={() => handleReaction(post.post_id, reaction.type)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all ${
                              isUserReaction 
                                ? `${reaction.activeColor} ${reaction.color} font-semibold ring-2 ring-offset-1 ring-current` 
                                : `${reaction.bgColor} ${reaction.color} hover:scale-105`
                            }`}
                            data-testid={`reaction-${reaction.type}-${post.post_id}`}
                          >
                            <Icon className="h-4 w-4" />
                            <span>{count}</span>
                          </button>
                        );
                      })
                    ) : (
                      <span className="text-sm text-[#9A9790] dark:text-[#6B7266]">
                        {language === 'he' ? 'היו הראשונים להגיב!' : 'Be the first to react!'}
                      </span>
                    )}
                  </div>
                  
                  {/* Add Reaction Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowReactionPicker(showReactionPicker === post.post_id ? null : post.post_id)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#4A5D43] dark:text-[#7DAA6B] bg-[#4A5D43]/10 dark:bg-[#4A5D43]/20 hover:bg-[#4A5D43]/20 rounded-full transition-all"
                      data-testid={`add-reaction-${post.post_id}`}
                    >
                      <Heart className="h-4 w-4" />
                      <span>{language === 'he' ? 'הגב' : 'React'}</span>
                    </button>
                    
                    {/* Reaction Picker */}
                    {showReactionPicker === post.post_id && (
                      <div className="absolute bottom-full left-0 mb-2 p-2 bg-white dark:bg-[#1a231a] rounded-xl shadow-xl border border-[#E8E4DC] dark:border-[#2a3a2a] flex gap-1 animate-fade-in-up z-10">
                        {REACTION_TYPES.map(reaction => {
                          const Icon = reaction.icon;
                          const isActive = postReactions[post.post_id]?.user_reactions?.includes(reaction.type);
                          
                          return (
                            <button
                              key={reaction.type}
                              onClick={() => handleReaction(post.post_id, reaction.type)}
                              className={`p-2 rounded-lg hover:scale-125 transition-all ${
                                isActive ? reaction.activeColor : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                              } ${reaction.color}`}
                              title={reaction.emoji}
                            >
                              <Icon className="h-5 w-5" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Comments Toggle */}
                <button
                  onClick={() => toggleComments(post.post_id)}
                  className="flex items-center gap-2 text-sm text-[#6B7266] hover:text-[#4A5D43] transition-colors"
                  data-testid={`toggle-comments-${post.post_id}`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {t.community.comments.title}
                </button>

                {/* Comments Section */}
                {expandedComments[post.post_id] && (
                  <div className="mt-5 pt-5 border-t border-[#E8E4DC]">
                    <div className="space-y-4 mb-4">
                      {expandedComments[post.post_id].length === 0 ? (
                        <p className="text-sm text-[#9A9790] text-center py-2">
                          {language === 'he' ? 'אין תגובות עדיין' : 'No comments yet'}
                        </p>
                      ) : (
                        expandedComments[post.post_id].map((comment) => (
                          <div key={comment.comment_id} className="flex gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={comment.user_picture} alt={comment.user_name} />
                              <AvatarFallback className="bg-[#4A5D43]/10">
                                <User className="h-3 w-3" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 bg-[#F5F2EB] rounded-xl p-3">
                              <p className="text-sm font-medium text-[#2C3329]">{comment.user_name}</p>
                              <p className="text-sm text-[#6B7266]">{comment.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {user ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={commentInputs[post.post_id] || ""}
                          onChange={(e) => setCommentInputs((prev) => ({ ...prev, [post.post_id]: e.target.value }))}
                          placeholder={t.community.comments.placeholder}
                          className="flex-1 px-4 py-2.5 text-sm bg-[#FAF8F5] border border-[#E8E4DC] rounded-full focus:outline-none focus:border-[#4A5D43]"
                          data-testid={`comment-input-${post.post_id}`}
                        />
                        <Button
                          onClick={() => handleSubmitComment(post.post_id)}
                          disabled={!commentInputs[post.post_id]?.trim()}
                          size="sm"
                          className="btn-primary rounded-full px-5"
                          data-testid={`submit-comment-${post.post_id}`}
                        >
                          {t.community.comments.submit}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-center text-[#9A9790]">
                        <button onClick={handleLogin} className="text-[#4A5D43] hover:underline">
                          {language === 'he' ? 'התחברו' : 'Log in'}
                        </button>
                        {language === 'he' ? ' כדי להגיב' : ' to comment'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Auth Section - LAST (for adding posts) */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#4A5D43] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : user ? (
          <>
            {/* User Card */}
            <div className="relative mb-6 p-5 rounded-2xl bg-gradient-to-br from-[#4A5D43] to-[#3A4A35] text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14 border-2 border-white/20 shadow-lg">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-white/10">
                      <User className="h-6 w-6 text-white" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-lg">{user.name}</p>
                    <p className="text-sm text-white/70">{user.email}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                >
                  <LogOut className={`h-4 w-4 ${language === 'he' ? 'ml-2' : 'mr-2'}`} />
                  {t.community.logoutButton}
                </Button>
              </div>
            </div>

            {/* New Post Form */}
            <div className="premium-card rounded-2xl p-6 border-2 border-dashed border-[#E8E4DC] hover:border-[#4A5D43]/30 transition-colors">
              <h3 className="font-serif text-lg text-[#2C3329] mb-4">
                {language === 'he' ? 'שתפו את הניסוי שלכם' : 'Share Your Experiment'}
              </h3>
              <Textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder={t.community.newPost.placeholder}
                className="mb-4 bg-[#FAF8F5] border-[#E8E4DC] focus:border-[#4A5D43] resize-none min-h-[120px] rounded-xl"
                data-testid="new-post-textarea"
              />
              
              {newPostImages.length > 0 && (
                <div className="flex gap-3 mb-4 flex-wrap">
                  {newPostImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={img} 
                        alt={`${language === 'he' ? 'תמונה' : 'Image'} ${index + 1}`}
                        className="w-24 h-24 object-cover rounded-xl border border-[#E8E4DC]"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <label className="cursor-pointer flex items-center gap-2 text-sm text-[#6B7266] hover:text-[#4A5D43] transition-colors px-3 py-2 rounded-full hover:bg-[#4A5D43]/5">
                  <ImagePlus className="h-5 w-5" />
                  {t.community.newPost.addImages}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={newPostImages.length >= 3}
                    data-testid="image-upload-input"
                  />
                </label>
                
                <Button
                  onClick={handleSubmitPost}
                  disabled={!newPostContent.trim() || isSubmitting}
                  data-testid="submit-post-btn"
                  className="btn-primary rounded-full px-8"
                >
                  <Send className={`h-4 w-4 ${language === 'he' ? 'ml-2' : 'mr-2'}`} />
                  {t.community.newPost.submit}
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Login Prompt - Beautiful Card */
          <div className="relative text-center py-12 px-6 rounded-2xl bg-gradient-to-br from-[#FAF8F5] to-[#F5F2EB] border border-[#E8E4DC] overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#4A5D43] via-[#C47F64] to-[#4A5D43]" />
            
            <div className="w-20 h-20 bg-[#4A5D43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-10 h-10 text-[#4A5D43]" />
            </div>
            
            <h3 className="font-serif text-2xl text-[#2C3329] mb-2">
              {language === 'he' ? 'רוצים לשתף ניסוי?' : 'Want to share an experiment?'}
            </h3>
            <p className="text-[#6B7266] mb-6 max-w-md mx-auto">
              {t.community.loginPrompt}
            </p>
            <Button 
              onClick={handleLogin}
              data-testid="google-login-btn"
              className="bg-white text-[#2C3329] border border-[#E8E4DC] hover:bg-[#F5F2EB] rounded-full px-8 py-5 shadow-md hover:shadow-lg transition-all"
            >
              <svg className={`${language === 'he' ? 'ml-3' : 'mr-3'} h-5 w-5`} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t.community.loginButton}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Community;
